import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/constants";
import { Sparkles, Clock, ShieldCheck, MapPin, Star, Phone } from "lucide-react";

export default function Landing() {
  const nav = useNavigate();
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    api.get("/vehicles").then(r => setVehicles(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page" className="bg-[#050505] min-h-screen text-[#F5F2EB]">
      <Navbar />

      {/* HERO */}
      <section data-testid="hero" className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1612065242504-3cf688429706?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85)" }}
        />
        <div className="absolute inset-0 z-10 bg-gradient-to-r from-black via-black/80 to-black/20" />
        <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
          <div className="overline mb-6">Est. {BRAND.est} · Premium Chauffeur &amp; Luxury Transportation</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight text-white max-w-3xl mb-8">
            Arrive with<br/>
            <span className="italic text-[#D4AF37]">distinction.</span>
          </h1>
          <p className="text-lg text-[#A1A1AA] max-w-xl mb-12 font-light leading-relaxed">
            Chauffeured sedans, SUVs, extended SUVs and 15-seater Sprinter vans for airport transfers, corporate travel, weddings and special events — across Wisconsin, Illinois and the wider Midwest.
          </p>
          <div className="flex flex-wrap gap-4">
            <button data-testid="hero-book" onClick={() => nav("/book")} className="btn-gold">Book Your Ride</button>
            <a href={`tel:${BRAND.phones[0].replace(/[^0-9+]/g, "")}`} data-testid="hero-call" className="btn-ghost">Call {BRAND.phones[0]}</a>
          </div>
          <div className="mt-6 text-xs">
            <span className="text-[#71717A]">Booking for your team? </span>
            <button onClick={() => nav("/corporate/signup")} data-testid="hero-corporate-link" className="text-[#D4AF37] hover:underline uppercase tracking-[0.18em]">
              Open a corporate account →
            </button>
          </div>
          <div className="mt-3 text-xs">
            <span className="text-[#71717A]">Wedding, prom, or multi-vehicle event? </span>
            <button onClick={() => nav("/events")} data-testid="hero-events-link" className="text-[#D4AF37] hover:underline uppercase tracking-[0.18em]">
              Book a special event →
            </button>
          </div>
          <div className="mt-16 flex flex-wrap gap-12 border-t border-white/5 pt-8">
            <Stat value="24/7" label="Around the clock" />
            <Stat value="100%" label="On-time guarantee" />
            <Stat value="3+" label="Years of service" />
            <Stat value="4.9★" label="800+ reviews" />
          </div>
        </div>
      </section>

      {/* FLEET */}
      <section id="fleet" data-testid="fleet-section" className="py-28 md:py-36 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <div className="overline mb-4">The Fleet</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none max-w-xl">A vehicle for every occasion.</h2>
            </div>
            <p className="text-[#A1A1AA] max-w-md text-base leading-relaxed">
              Late-model black sedans, SUVs, extended SUVs and 15-seater Sprinter vans — meticulously maintained and chauffeur-driven.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {vehicles.map((v) => (
              <article
                key={v.vehicle_id}
                data-testid={`fleet-card-${v.vehicle_id}`}
                className="dlx-card group cursor-pointer"
                onClick={() => nav("/book")}
              >
                <div className="aspect-[5/4] overflow-hidden bg-black">
                  <img src={v.image_url} alt={v.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"/>
                </div>
                <div className="p-7 border-t border-white/5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-serif text-2xl">{v.name}</h3>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">Starting From</div>
                      <span className="text-[#D4AF37] font-serif text-2xl leading-tight">${(v.min_fare || 0).toFixed(0)}</span>
                    </div>
                  </div>
                  <p className="text-sm text-[#71717A] mb-5">{v.subtitle}</p>
                  <div className="flex items-center gap-4 text-xs text-[#A1A1AA] uppercase tracking-[0.16em]">
                    <span>{v.pax} pax</span>
                    <span className="w-px h-3 bg-white/10"/>
                    <span>{v.luggage} bags</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" data-testid="services-section" className="py-28 md:py-36 px-6 md:px-12 bg-[#080808]">
        <div className="max-w-7xl mx-auto">
          <div className="overline mb-4">What We Do</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none mb-20 max-w-2xl">Services crafted for every journey.</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                img: "/services/airport.jpg", to: "/book", id: "airport",
                title: "Airport Transfers",
                body: "Flight-tracked, punctual pick-ups and drop-offs at MKE, ORD, MDW & MSN — stress-free, 24/7.",
              },
              {
                img: "/services/corporate.jpg", to: "/corporate/signup", id: "corporate",
                title: "Corporate Travel",
                body: "Arrive on time and in style for every meeting — executive travel, roadshows and corporate billing.",
              },
              {
                img: "/services/events.jpg", to: "/events", id: "events",
                title: "Weddings & Events",
                body: "Weddings, proms, galas and VIP occasions — tailored multi-vehicle packages.",
              },
              {
                img: "/services/city.jpg", to: "/book", id: "city",
                title: "City to City",
                body: "Comfortable long-distance travel — Milwaukee, Chicago, Madison, Green Bay & beyond.",
              },
            ].map(s => (
              <article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className="dlx-card group cursor-pointer flex flex-col"
                onClick={() => nav(s.to)}
              >
                <div className="aspect-[4/3.4] overflow-hidden bg-black">
                  <img
                    src={s.img}
                    alt={s.title}
                    loading="lazy"
                    className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                  />
                </div>
                <div className="p-6 border-t border-white/5 flex flex-col flex-1">
                  <h3 className="font-serif text-2xl mb-3">{s.title}</h3>
                  <p className="text-sm text-[#A1A1AA] leading-relaxed mb-6 flex-1">{s.body}</p>
                  <button
                    data-testid={`service-learn-more-${s.id}`}
                    onClick={(e) => { e.stopPropagation(); nav(s.to); }}
                    className="self-start text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] border border-[#D4AF37]/40 px-5 py-2.5 hover:bg-[#D4AF37] hover:text-[#050505] transition-colors"
                  >
                    Learn more
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section data-testid="why-section" className="py-28 md:py-36 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="overline mb-4">Why Delux</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none mb-20 max-w-2xl">The standard of premium ground travel.</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
            <Why icon={<Clock size={26}/>} title="Always On Time" body="On-time guarantee with flight tracking and real-time monitoring."/>
            <Why icon={<ShieldCheck size={26}/>} title="Vetted Chauffeurs" body="Licensed, background-checked, uniformed professionals."/>
            <Why icon={<Sparkles size={26}/>} title="Upfront Pricing" body="Flat-rate quotes. No surge pricing. No hidden fees."/>
            <Why icon={<Star size={26}/>} title="Spotless Vehicles" body="Detailed before every ride. Bottled water and amenities included."/>
            <Why icon={<MapPin size={26}/>} title="Midwest & Beyond" body="Wisconsin & Illinois base with nationwide affiliate coverage."/>
            <Why icon={<Phone size={26}/>} title="24/7 Concierge" body="Live human dispatch — call, text or email anytime."/>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-section" className="py-28 md:py-36 px-6 md:px-12 bg-[#080808]">
        <div className="max-w-7xl mx-auto">
          <div className="overline mb-4">How It Works</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none mb-20 max-w-2xl">Three steps to a seamless ride.</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              ["01", "Request a Quote", "Tell us where, when and how many. Get a flat-rate price within minutes.", "/how/step1.jpg"],
              ["02", "Confirm & Relax", "Receive driver details and a tracking link. Your chauffeur arrives early.", "/how/step2.jpg"],
              ["03", "Arrive in Style", "Settle in. Enjoy the ride. We handle the rest, door-to-door.", "/how/step3.jpg"],
            ].map(([n, t, b, img]) => (
              <div key={n} data-testid={`how-step-${n}`} className="group">
                <div className="relative mb-8 overflow-hidden">
                  <div className="aspect-[4/3] bg-black">
                    <img
                      src={img}
                      alt={t}
                      loading="lazy"
                      className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 bg-[#050505]/90 backdrop-blur-sm px-5 py-2 border-t border-r border-[#D4AF37]/40">
                    <span className="font-serif text-[#D4AF37] text-4xl">{n}</span>
                  </div>
                </div>
                <h3 className="font-serif text-2xl mb-3">{t}</h3>
                <p className="text-[#A1A1AA] leading-relaxed text-sm">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials" className="py-28 md:py-36 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="overline mb-4">Testimonials</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none mb-4 max-w-2xl">Trusted by discerning travelers.</h2>
          <p className="text-[#D4AF37] mb-16">4.9 / 5 · 800+ reviews</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              ["Delux is the only car service my team uses for executive travel. Drivers are always early, professional and discreet.", "Marcus T.", "VP Operations, Milwaukee"],
              ["Booked the Sprinter for our wedding day — punctual, immaculate and stress-free. The chauffeur went above and beyond.", "Elena R.", "Bride · Lake Geneva"],
              ["I fly into MKE and O'Hare twice a month and Delux is faster than rideshare, with none of the surge pricing. Just a great service.", "James K.", "Frequent Flyer"],
            ].map(([q, n, t]) => (
              <blockquote key={n} className="border border-white/5 p-8 bg-[#0A0A0A]">
                <span className="font-serif text-[#D4AF37] text-5xl leading-none block mb-4">“</span>
                <p className="text-[#F5F2EB] leading-relaxed mb-6 italic font-light">{q}</p>
                <div className="text-sm uppercase tracking-[0.18em] text-[#D4AF37]">{n}</div>
                <div className="text-xs text-[#71717A]">{t}</div>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto border border-[#D4AF37]/40 p-12 md:p-20 text-center relative overflow-hidden">
          <div className="overline mb-4">Reserve in minutes</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight mb-8">Let&apos;s plan your next ride.</h2>
          <p className="text-[#A1A1AA] mb-10 max-w-xl mx-auto">Live human dispatch · No payment required to request a quote · Replies within minutes.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link data-testid="cta-book" to="/book" className="btn-gold">Book a Ride</Link>
            <a href={`tel:${BRAND.phones[0].replace(/[^0-9+]/g, "")}`} className="btn-ghost">Call {BRAND.phones[0]}</a>
          </div>
        </div>
      </section>

      <Footer/>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-serif text-3xl text-[#D4AF37]">{value}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mt-1">{label}</div>
    </div>
  );
}

function Why({ icon, title, body }) {
  return (
    <div>
      <div className="text-[#D4AF37] mb-4">{icon}</div>
      <h3 className="font-serif text-xl mb-2">{title}</h3>
      <p className="text-[#A1A1AA] text-sm leading-relaxed">{body}</p>
    </div>
  );
}
